/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ActionContext, Module } from 'vuex';
import store from '@/store';
import { Toast } from '@hb/coffee';

import type RootStateType from '@/store/types';
import generateMutations, {
  UPDATE_STATE_TYPE,
} from '@/store/generate-mutations';
import { RightPackageTypeEnum } from '@/api/rights/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { getGalileoAb } from '@/shared/galileoAb';
import { SuperMemberPopupAbExperimentResEnum } from '@/enums/ziyinbAbexperiment';
import { rightMonitorReport } from '@/shared/right-utils/rightMonitor';
import ProductCommodity from '@/shared/commodity';
import { getSuperMemberCardSummary } from '@/api/rights';
import createPersistedState from 'vuex-persistedstate';
import { financeStorageKeyPrefix } from '@/constant';
import { Md5 } from 'ts-md5';
import type { ApplyChannelData, CalcPlanListData } from '@/api/apply-product/types';
import { RateTypeEnum } from '@/api/apply-product/types';
import { getSummaryDataHandler } from './components/rights/zhenxiang-card/utils';
import type { LoanRightStateType } from './types';
import { loanAndRightsParamasMap } from './components/rights/zhenxiang-card/check';
import { getPricingTypeByProductId } from './utils';
import { isCanBuyRightRateType } from './const';

const moduleStore: Module<LoanRightStateType, RootStateType> = {
  namespaced: true,

  state: {
    /** 权益包信息，由查询后端接口返回后获取 */
    rightParams: null,
    /** 是否勾选了当前的臻享卡权益 */
    isRightTick: false,
    /** 权益卡片面板tab切换在极速模式 */
    showSpeed: false,
    /** 倒计时秒数，用户在命中权益豁免时，如果接口返回了倒计时秒数，则在臻享卡的权益Tab上展示倒计时 */
    recordCountDownTime: 0,
    /** 折扣豁免倒计时是否到期 */
    isCountingDownFinished: false,
    /** 展示了超级会员的用户是否勾选了协议 */
    isSuperMemberCheck: null,
    showSuperMemberCard: false,
    superMemberInfo: null,
  },
  getters: {
    /** 是否为自营有权益资方(24+、20+)单通道模式 */
    isNeedRightCapitalSinglePassage(): boolean {
      const { channel } = store.state.applyProduct;
      return channel?.productId === ProductCommodity.AUTARKY_B_COMMODITY
        && isCanBuyRightRateType(channel?.rateType)
        && channel?.needRight === true
        && channel?.capitalList?.length === 1;
    },
  },
  actions: {
    updateShowSpeed(context: ActionContext<LoanRightStateType, RootStateType>, showSpeed: boolean) {
      context.dispatch(UPDATE_STATE_TYPE, {
        showSpeed,
      });
    },
    async getZhenxiangMemberCardSummary(
      context: ActionContext<LoanRightStateType, RootStateType>,
      payload: {
        channel?: ApplyChannelData;
      },
    ) {
      const { channel } = payload;
      const res = await getSuperMemberCardSummary({
        productId: channel?.productId,
        packageType: RightPackageTypeEnum.ZHEN_XIANG_MEMBER_CARD,
      });
      // 埋点打入返回接口数据
      rightMonitorReport(
        'get_response_data',
        res,
      );
      // 增加赋值判断保证赋值严谨性
      if (channel?.productId === store.state.applyProduct.channel?.productId) {
        context.dispatch(UPDATE_STATE_TYPE, {
          rightParams: res,
        });
      }
    },
    /** 获取权益包详情并且赋值 */
    async getRightSummaryData(
      context: ActionContext<LoanRightStateType, RootStateType>,
      payload: {
        getzhenxiangMember?: boolean;
        /** 调用侧传入的利率字段 */
        planListData?: CalcPlanListData | null;
      },
    )
      :Promise<void> {
      const {
        amount, period, channel, planListData,
      } = store.state.applyProduct;
      const planListDataRequest = payload?.planListData || planListData;
      // 单通道24+购买权益，右侧切换至轻资产，无需请求 getRightSummaryData 接口
      if (store.state.applyProduct.is24NeedRightAndNormalIs36) {
        return;
      }
      if (channel && channel.productId && amount && period) {
        const params = {
          orderAmount: Number(amount),
          period: Number(period && period.value),
          productId: (channel && channel.productId),
          capitalCode: (channel && channel.capitalCode),
          sourceId: (channel && channel.sourceId) || '',
          packageType: RightPackageTypeEnum.CHANNEL_B,
          pricingType: getPricingTypeByProductId(channel?.productId),
          // https://metis2.hellobike.cn/browse/SKHYQY-1550,增加利率字段来匹配权益包
          rateValue: planListDataRequest?.rateValue,
        };

        // 权益包数据获取
        if (store.getters['applyProduct/needBuyZhenxiangMember']) {
          context.dispatch('getZhenxiangMemberCardSummary', {
            channel,
          });
        } else {
          const res = await getSummaryDataHandler(params);

          // 存储请求参数，用于请求权益包详情
          if (res?.packageId) {
            loanAndRightsParamasMap.set(res?.packageId, params);
          }

          // 如果请求成功取消toast展示；
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          Toast.clear();

          // 埋点打入返回接口数据
          rightMonitorReport(
            'get_response_data',
            res,
          );

          context.dispatch(UPDATE_STATE_TYPE, {
            rightParams: res,
          });

          // 埋点打入赋值后store中的数据
          rightMonitorReport(
            'set_store_value',
            context.state.rightParams,
          );

          /**
         * 如果倒计时存在：赋值 并将 isCountingDownFinished 值更新
         * 若有值则代表倒计时未结束
         */
          if (res.countDownTime) {
            context.dispatch(UPDATE_STATE_TYPE, {
              recordCountDownTime: res.countDownTime,
              isCountingDownFinished: false,
            });
          } else {
            context.dispatch(UPDATE_STATE_TYPE, {
              recordCountDownTime: 0,
            });
          }
        }
      } else {
        ubtSendError('因必填参数获取为空，导致获取权益包详情失败');
      }
    },
    initSuperMemberCardState(
      context: ActionContext<LoanRightStateType, RootStateType>,
    ) {
      // 切换产品时重置勾选状态
      context.dispatch(UPDATE_STATE_TYPE, {
        isSuperMemberCheck: false,
        rightParams: null,
      });
    },
    async needBuySuperMemberCheck(
      context: ActionContext<LoanRightStateType, RootStateType>,
      payload: {
        channel: ApplyChannelData;
        planListData: CalcPlanListData;
      },
    ) {
      const { channel, planListData } = payload;
      const needBuySuperMember = store.getters['applyProduct/needBuySuperMember'] as unknown as boolean;
      const needBuyRight = store.getters['applyProduct/needBuyRight'] as unknown as boolean;
      const { productId = '' } = channel || {};
      const rateValue = planListData?.rateValue;
      if (productId) {
        const isAutarkyNeedBuySuperMember = (
          productId === ProductCommodity.AUTARKY_B_COMMODITY && needBuySuperMember
        );

        // 在前端识别到需要同时购买臻享卡 和 超级会员时，上报error事件
        if (isAutarkyNeedBuySuperMember && needBuyRight) {
          ubtSendError({
            name: 'finance_loan_withdrawal_rights_error',
            message: 'bothTrue',
          });
        }

        // 排除掉自营8180的不需要购买超级会员的数据请求
        // 排除掉需要购买臻享卡的超级会员接口请求，优先保证臻享卡购买卡片渲染
        if ((isAutarkyNeedBuySuperMember && !needBuyRight)
          || productId !== ProductCommodity.AUTARKY_B_COMMODITY) {
          try {
            const info = await getSuperMemberCardSummary({
              productId,
              rateValue,
              pricingType: planListData?.pricingType,
              /** 1.0.0开始支持版本超级会员PLUS卡流程，目前只在提现环节分发，后续增加其他入口 */
              bizVersion: '1.0.0',
            });
            context.dispatch(UPDATE_STATE_TYPE, {
              superMemberInfo: info || null,
              // 如果超会接口返回数据，则显示超会组件
              showSuperMemberCard: info && Object.keys(info).length > 0,
            });
          } catch (e) {
            context.dispatch(UPDATE_STATE_TYPE, {
              superMemberInfo: null,
              showSuperMemberCard: false,
            });
          }
        } else {
          context.dispatch(UPDATE_STATE_TYPE, {
            superMemberInfo: null,
          });
        }
      }
    },
  },
};

store.registerModule(
  'loanRight',
  generateMutations<LoanRightStateType>(moduleStore),
  {
    preserveState: !!store.state.loanRight,
  },
);

// 动态注册完后再持久化数据，以解决无初始值的问题
const rightKey = (store.state.base.originUserInfo
    && store.state.base.originUserInfo.mobile) || store.state.base.userId;
const rightPersistedKey = `${financeStorageKeyPrefix}_${Md5.hashStr(rightKey)}`;
const rightPersistedKeyLast = `${financeStorageKeyPrefix}_${Md5.hashStr(rightPersistedKey)}`;
// 权益数据单独存储
createPersistedState({
  key: rightPersistedKeyLast,
  paths: [
    'loanRight',
  ],
})(store);
